import * as React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  DetailShell,
  HCard,
  HCardButton,
  HPrimaryButton,
  HSecondaryButton,
  HGhostButton,
  HButtonPair,
  SerifHeading,
  SectionLabel,
  FieldLabel,
} from '../h';
import ErrorModal from '../ui/ErrorModal';
import { auth, API_BASE_URL, ACCOUNT_DOMAIN } from '../../firebase/firebaseConfig';

/* HearthSetupView — FEAT-133 v9 spec §3.1-3.4.
   Four screens state-machined in a single component:
     - picker   (3.1) Setup path picker
     - wifi     (3.2) WiFi entry (owner path)
     - qr       (3.3) QR display (owner path) — real passcode + QR render
     - sendLink (3.4) Send setup link (non-owner path) — real one-time link

   Backend wiring ported from the legacy DeviceSetup.js:
     - wifi stage calls POST /user/generatePasscode { type: 'deviceSetup' } on
       continue and moves to the QR stage. The QR payload encodes SSID,
       password, familyUserID (familyID@family.higlo.ai), and passcode in the
       four-field format the Hearth app expects.
     - sendLink stage calls the same endpoint with { type: 'guestSetup' } and
       base64-encodes {token, fID, fName, ts, pad} into a /deviceSetupLocal
       URL under ACCOUNT_DOMAIN.
*/

/* Progress strip: three pills, first N filled. */
function ProgressStrip({ step, total = 3, caption }) {
  return (
    <div className="px-4 mb-6">
      <div className="flex items-center gap-1.5">
        {Array.from({ length: total }).map((_, idx) => (
          <div
            key={idx}
            className={
              'flex-1 h-1 rounded-full ' +
              (idx < step ? 'bg-slate-900' : 'bg-slate-200')
            }
          />
        ))}
      </div>
      {caption && (
        <div className="text-[11px] text-slate-400 mt-1.5">{caption}</div>
      )}
    </div>
  );
}

/* Inline breadcrumb block for the DetailShell top-bar.
   Two-line stacked label (small caps label on top, bold screen title below). */
function Breadcrumb({ label, title }) {
  return (
    <div className="leading-tight">
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
        {label}
      </div>
      <div className="text-[14px] font-semibold text-slate-900">{title}</div>
    </div>
  );
}

/* Icon helpers (lucide-matched stroke icons used in the mockup). */
const iconBase = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

function DisplayIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      {...iconBase}
    >
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="7" y1="21" x2="17" y2="21" />
    </svg>
  );
}

function LinkIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...iconBase}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function ChevronRight({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...iconBase}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function WifiIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...iconBase}>
      <path d="M5 12.55a11 11 0 0 1 14.08 0" />
      <path d="M1.42 9a16 16 0 0 1 21.16 0" />
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
      <line x1="12" y1="20" x2="12.01" y2="20" />
    </svg>
  );
}

function EyeIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...iconBase}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function InfoIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...iconBase}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

/* ────────── Shared helper: call POST /user/generatePasscode ────────── */

async function callGeneratePasscode({ familyID, type }) {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('You must be signed in to set up a Hearth.');
  }
  const idToken = await currentUser.getIdToken();
  const response = await fetch(`${API_BASE_URL}/user/generatePasscode`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      userEmail: currentUser.email,
      familyID,
      type,
    }),
  });
  const data = await response.json();
  if (!data || !data.success) {
    throw new Error(data?.error || 'Request failed. Please try again.');
  }
  return data;
}

/* ────────── Stage 3.1 — Setup path picker ────────── */

function PickerStage({ familyName, onPickOwner, onPickOther, onBack }) {
  return (
    <DetailShell
      onBack={onBack}
      breadcrumb={<Breadcrumb label="Setting up" title={familyName} />}
    >
      <div className="h-px bg-slate-200 mx-4 mb-6" />
      <div className="px-4 pt-3 pb-6">
        <SerifHeading className="mb-3">
          Let{'\u2019'}s get this Hearth connected.
        </SerifHeading>
        <p className="text-[13px] text-slate-600 leading-relaxed mb-6">
          Are <strong>you</strong> with the Hearth right now, or is someone else
          setting it up?
        </p>

        <HCardButton
          onClick={onPickOwner}
          className="p-3 mb-3"
          style={{ borderWidth: 2, borderColor: '#0f172a' }}
        >
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: '#0f172a', color: 'white' }}
            >
              <DisplayIcon className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="text-[15px] font-semibold text-slate-900 mb-0.5">
                You{'\u2019'}re with the Hearth
              </div>
              <div className="text-[12px] text-slate-500 leading-relaxed">
                You can see the TV. We{'\u2019'}ll connect it together.
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400 mt-1" />
          </div>
        </HCardButton>

        <HCardButton onClick={onPickOther} className="p-3 mb-4">
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: '#f1f5f9', color: '#0f172a' }}
            >
              <LinkIcon className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="text-[15px] font-semibold text-slate-900 mb-0.5">
                Someone else will set it up
              </div>
              <div className="text-[12px] text-slate-500 leading-relaxed">
                Send them a link they can use on their phone.
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400 mt-1" />
          </div>
        </HCardButton>

        <div className="text-[11px] text-slate-400 text-center">
          You can always come back here later.
        </div>
      </div>
    </DetailShell>
  );
}

/* ────────── Stage 3.2 — WiFi entry ────────── */

function WifiStage({
  familyName,
  initialSsid = '',
  initialPassword = '',
  // Parent owns the in-flight state for /user/generatePasscode now (the
  // call moved out of QrStage to avoid the StrictMode double-fetch race).
  // We just disable the buttons + show the error.
  submitting = false,
  errorMessage: parentError = '',
  onDismissError,
  onBack,
  onContinue,
  onSkip,
}) {
  // Seed from parent-held state so that tapping Back on the QR screen
  // returns the user to the same SSID / password they already typed — lets
  // them fix a typo without retyping the whole lot.
  const [ssid, setSsid] = React.useState(initialSsid);
  const [password, setPassword] = React.useState(initialPassword);
  const [showPw, setShowPw] = React.useState(false);
  const [localError, setLocalError] = React.useState('');
  const [showErrorModal, setShowErrorModal] = React.useState(false);

  // Local validation errors come from this component; passcode-generation
  // errors come from the parent. Surface either through the same modal.
  const errorMessage = localError || parentError;
  React.useEffect(() => {
    if (parentError) setShowErrorModal(true);
  }, [parentError]);

  // WiFi details are not POSTed on their own — they're carried into the QR
  // payload (see QrStage). Validation matches the legacy DeviceSetup.js: the
  // SSID cannot be empty (semicolons break the QR format so we reject them).
  const handleContinue = (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!ssid) {
      setLocalError('WiFi SSID cannot be empty.');
      setShowErrorModal(true);
      return;
    }
    if (ssid.includes(';')) {
      setLocalError(
        'Your WiFi network name contains a semicolon (;) which is not supported. Please rename your WiFi network or use a different network.'
      );
      setShowErrorModal(true);
      return;
    }
    setLocalError('');
    onContinue({ ssid, password });
  };

  const handleSkip = () => {
    if (submitting) return;
    onSkip();
  };

  return (
    <DetailShell
      onBack={onBack}
      breadcrumb={
        <Breadcrumb label={`Setting up ${familyName}`} title="WiFi details" />
      }
    >
      <ProgressStrip
        step={2}
        caption={`Step 2 of 3 \u00B7 Confirm TV \u2192 WiFi \u2192 Scan QR`}
      />
      <form className="px-4 pt-3 pb-6" onSubmit={handleContinue}>
        <SerifHeading size="md" className="mb-3">
          What{'\u2019'}s your home WiFi?
        </SerifHeading>
        <p className="text-[13px] text-slate-600 leading-relaxed mb-4">
          Your Hearth will use this to get online. If it{'\u2019'}s already
          connected, skip.
        </p>

        <FieldLabel htmlFor="hsv-ssid" className="mb-1.5">
          Network name (SSID)
        </FieldLabel>
        <HCard className="px-3 py-2.5 flex items-center gap-2 mb-3">
          <WifiIcon className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <input
            id="hsv-ssid"
            className="flex-1 text-[14px] outline-none bg-transparent text-slate-900 placeholder:text-slate-400"
            value={ssid}
            onChange={(e) => setSsid(e.target.value)}
            placeholder="Your WiFi name"
            autoComplete="off"
            disabled={submitting}
          />
        </HCard>
        <p className="text-[11px] text-slate-400 mb-4 leading-relaxed">
          Type it exactly as it appears on your router {'\u2014'} case and
          spaces matter.
        </p>

        <FieldLabel htmlFor="hsv-pw" className="mb-1.5">
          Password
        </FieldLabel>
        <HCard className="px-3 py-2.5 flex items-center gap-2 mb-4">
          <input
            id="hsv-pw"
            type={showPw ? 'text' : 'password'}
            className="flex-1 text-[14px] outline-none bg-transparent text-slate-900 placeholder:text-slate-400"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="WiFi password"
            autoComplete="off"
            disabled={submitting}
          />
          <button
            type="button"
            onClick={() => setShowPw((v) => !v)}
            className="text-slate-500 hover:text-slate-700 flex-shrink-0"
            aria-label={showPw ? 'Hide password' : 'Show password'}
          >
            <EyeIcon className="w-4 h-4" />
          </button>
        </HCard>

        <HButtonPair className="mb-2">
          <HSecondaryButton type="button" onClick={onBack} disabled={submitting}>
            Back
          </HSecondaryButton>
          <HPrimaryButton type="submit" disabled={submitting}>
            {submitting ? 'Working\u2026' : 'Continue'}
          </HPrimaryButton>
        </HButtonPair>
        <HGhostButton
          type="button"
          onClick={handleSkip}
          className="w-full text-[13px]"
          disabled={submitting}
        >
          My Hearth is already online {'\u2014'} skip
        </HGhostButton>
      </form>

      <ErrorModal
        isOpen={showErrorModal}
        message={errorMessage}
        onClose={() => {
          setShowErrorModal(false);
          setLocalError('');
          onDismissError?.();
        }}
      />
    </DetailShell>
  );
}

/* ────────── Stage 3.3 — QR display ────────── */

function QrStage({ familyID, familyName, ssid, password, passcode, onBack, onDone }) {
  // Passcode is generated ONCE by the parent (HearthSetupView) when the
  // WiFi-step button is clicked. QrStage is pure render — no fetch, no
  // side-effect. This matches the legacy DeviceSetup.js click-handler
  // flow and avoids the React 18 StrictMode double-mount race where two
  // /user/generatePasscode calls would overwrite
  // `passcodes/{familyUserID}` in Firestore out of order relative to
  // what the rendered QR captured.

  // Stable familyUserID for the Hearth sign-in (mirror of the legacy format).
  const familyUserID = `${familyID}@family.higlo.ai`;

  // QR payload — IDENTICAL to OLD DeviceSetup.js. Four semicolon-
  // terminated fields passed as an array of segments:
  //   `${ssid};`, `${password};`, `${familyUserID};`, `${passcode};`
  // The TVApp splits the decoded text on `;` and reads four fields in
  // order. Don't reformat / collapse / change this — keep it byte-for-byte
  // matched to the legacy webapp the TVApp is known to scan correctly.
  const qrValue = passcode
    ? [
        `${ssid};`,
        `${password};`,
        `${familyUserID};`,
        `${passcode};`,
      ]
    : null;

  return (
    <DetailShell
      onBack={onBack}
      breadcrumb={
        <Breadcrumb
          label={`Setting up ${familyName}`}
          title="Show this QR to your Hearth"
        />
      }
    >
      <ProgressStrip
        step={3}
        caption={`Step 3 of 3 \u00B7 Confirm TV \u2192 WiFi \u2192 Scan QR`}
      />
      <div className="px-4 pt-3 pb-6">
        <SerifHeading size="md" className="mb-2">
          Show this QR to your Hearth
        </SerifHeading>
        <p className="text-[13px] text-slate-600 leading-relaxed mb-4">
          {ssid
            ? 'Hold your phone up to the Hearth\u2019s camera. It\u2019ll connect to WiFi and sign in. '
            : 'Hold your phone up to the Hearth\u2019s camera to sign in (WiFi setup will be skipped). '}
          <strong>This code is valid for 2 hours.</strong>
        </p>

        {/* Kept at p-4 — the QR card hosts a 230px render and needs the extra
            breathing room per the styling note in the task. */}
        {/* Passcode is always supplied by the parent before this stage
            mounts, so we always render the QR. No loading / error state
            here. */}
        <HCard className="p-4 flex justify-center mb-4">
          <QRCodeSVG
            value={qrValue}
            size={280}
            id="hearth-setup-qr"
          />
        </HCard>

        <HCard variant="next" className="p-3 mb-4 flex items-start gap-2">
          <InfoIcon
            className="w-4 h-4 flex-shrink-0 mt-0.5"
            style={{ color: '#1e40af' }}
          />
          <div className="text-[12px] text-slate-700 leading-relaxed">
            Once the Hearth scans this, it{'\u2019'}ll join your WiFi and appear
            on your dashboard as ready.
          </div>
        </HCard>

        <HButtonPair>
          <HSecondaryButton onClick={onBack}>Back</HSecondaryButton>
          <HPrimaryButton onClick={onDone}>
            I{'\u2019'}m done
          </HPrimaryButton>
        </HButtonPair>
        <p className="text-[11px] text-slate-400 mt-3 text-center">
          Tap Back to fix WiFi if the Hearth can{'\u2019'}t connect.
        </p>
      </div>
    </DetailShell>
  );
}

/* ────────── Stage 3.4 — Send setup link ────────── */

function SendLinkStage({ familyID, familyName, onBack, previewLink }) {
  const [link, setLink] = React.useState(previewLink || '');
  const [copied, setCopied] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [showErrorModal, setShowErrorModal] = React.useState(false);

  // Build a guest setup URL from a POST /user/generatePasscode response.
  // Mirrors the legacy handleSubmitInviteSetup exactly (same params / trailerPad).
  const generate = React.useCallback(async () => {
    // Preview short-circuit: if a fake link was supplied, never call the
    // backend. Keeps the Send-link screenshot renderable without auth.
    if (previewLink) {
      setLink(previewLink);
      return;
    }
    setLoading(true);
    setLink('');
    try {
      const data = await callGeneratePasscode({
        familyID,
        type: 'guestSetup',
      });
      const trailerPad = '11';
      const params = {
        token: data.code,
        fID: familyID,
        fName: familyName,
        ts: data.timestamp?._seconds,
        pad: trailerPad,
      };
      const encodedParams = btoa(JSON.stringify(params));
      const guestUrl = new URL('/deviceSetupLocal', ACCOUNT_DOMAIN);
      guestUrl.search = new URLSearchParams({ data: encodedParams }).toString();
      setLink(guestUrl.toString());
    } catch (err) {
      setErrorMessage(
        err?.message || 'Failed to generate Guest link. Please try again.'
      );
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  }, [familyID, familyName, previewLink]);

  React.useEffect(() => {
    generate();
  }, [generate]);

  const handleCopy = async () => {
    if (!link) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard may be blocked — silently ignore
    }
  };

  const handleShare = async () => {
    if (!link) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Set up ${familyName}`,
          text: `Set up the ${familyName} Hearth`,
          url: link,
        });
      } catch {
        // user cancelled, nothing to do
      }
    } else {
      handleCopy();
    }
  };

  const handleRegenerate = () => {
    setCopied(false);
    generate();
  };

  return (
    <DetailShell
      onBack={onBack}
      breadcrumb={
        <Breadcrumb
          label={`Setting up ${familyName}`}
          title="Send a setup link"
        />
      }
    >
      <div className="h-px bg-slate-200 mx-4 mb-4" />
      <div className="px-4 pt-3 pb-6">
        <SerifHeading className="mb-3">
          Let them set it up themselves.
        </SerifHeading>
        <p className="text-[13px] text-slate-600 leading-relaxed mb-4">
          Send this one-time link to whoever{'\u2019'}s setting up{' '}
          <strong>{familyName}</strong>. They tap it on their phone, connect to
          WiFi, and scan the QR at their Hearth.
        </p>

        <HCard className="p-3 mb-3">
          <SectionLabel className="mb-2">
            One-time link {'\u00B7'} expires in 7 days
          </SectionLabel>
          {loading ? (
            <div className="flex items-center gap-2 bg-slate-50 rounded p-2 mb-3">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-900" />
              <span className="text-[12px] text-slate-500">
                Generating link{'\u2026'}
              </span>
            </div>
          ) : (
            <div className="text-[12px] text-slate-600 break-all font-mono bg-slate-50 rounded p-2 mb-3">
              {link || '\u2014'}
            </div>
          )}
          <HButtonPair>
            <HPrimaryButton
              type="button"
              onClick={handleCopy}
              className="py-[9px] text-[13px]"
              disabled={loading || !link}
            >
              {copied ? 'Copied!' : 'Copy link'}
            </HPrimaryButton>
            <HSecondaryButton
              type="button"
              onClick={handleShare}
              className="py-[9px] text-[13px]"
              disabled={loading || !link}
            >
              Share{'\u2026'}
            </HSecondaryButton>
          </HButtonPair>
        </HCard>

        <HCard variant="next" className="p-3 mb-4">
          <div className="text-[12px] text-slate-700 leading-relaxed">
            <strong>While they{'\u2019'}re setting up</strong>, come back to the
            dashboard. The Hearth will turn ready once it{'\u2019'}s online.
          </div>
        </HCard>

        <HGhostButton
          type="button"
          onClick={handleRegenerate}
          className="w-full text-[13px]"
          style={{ color: '#64748b' }}
          disabled={loading}
        >
          Re-generate link
        </HGhostButton>
      </div>

      <ErrorModal
        isOpen={showErrorModal}
        message={errorMessage}
        onClose={() => {
          setShowErrorModal(false);
          setErrorMessage('');
        }}
      />
    </DetailShell>
  );
}

/* ────────── HearthSetupView — top-level state machine ────────── */

export function HearthSetupView({
  familyID,
  familyName,
  onDone,
  onCancel,
  initialStage = 'picker',
  // Preview-only: supply a fake passcode + link so the QR and Send-link
  // stages render without calling the backend. Real usage leaves these
  // undefined and the component hits /user/generatePasscode.
  previewPasscode,
  previewLink,
}) {
  const [stage, setStage] = React.useState(initialStage);
  // Carry WiFi credentials from the WiFi stage into the QR stage.
  const [wifi, setWifi] = React.useState({ ssid: '', password: '' });
  // Passcode is generated ONCE — when the user leaves the WiFi step (by
  // clicking Continue or Skip), NOT inside QrStage's useEffect. An earlier
  // version fetched inside the QR stage on mount; under React 18 StrictMode
  // the effect mounts twice and fires /user/generatePasscode twice. Each
  // call overwrites `passcodes/{familyUserID}` in Firestore, and the two
  // Firestore writes can land out of order relative to the client responses
  // — leaving the QR showing code A while Firestore holds code B. TVApp
  // then rejects the scan with "Invalid sign-in code". Moving the call to
  // the click handler matches the legacy DeviceSetup.js flow and
  // guarantees one fetch per QR display.
  const [passcode, setPasscode] = React.useState(previewPasscode || '');
  const [generating, setGenerating] = React.useState(false);
  const [generateError, setGenerateError] = React.useState('');

  const advanceToQr = React.useCallback(
    async (creds) => {
      setGenerateError('');
      setWifi(creds);
      // Preview / mockup path: skip the backend call and go straight in
      // with the pre-supplied passcode.
      if (previewPasscode) {
        setPasscode(previewPasscode);
        setStage('qr');
        return;
      }
      setGenerating(true);
      try {
        const data = await callGeneratePasscode({
          familyID,
          type: 'deviceSetup',
        });
        setPasscode(data.code);
        setStage('qr');
      } catch (err) {
        setGenerateError(
          err?.message || 'Failed to generate sign-in code. Please try again.',
        );
      } finally {
        setGenerating(false);
      }
    },
    [familyID, previewPasscode],
  );

  switch (stage) {
    case 'wifi':
      return (
        <WifiStage
          familyName={familyName}
          initialSsid={wifi.ssid}
          initialPassword={wifi.password}
          submitting={generating}
          errorMessage={generateError}
          onDismissError={() => setGenerateError('')}
          onBack={() => setStage('picker')}
          onContinue={(creds) => advanceToQr(creds)}
          onSkip={() => advanceToQr({ ssid: '', password: '' })}
        />
      );
    case 'qr':
      return (
        <QrStage
          familyID={familyID}
          familyName={familyName}
          ssid={wifi.ssid}
          password={wifi.password}
          passcode={passcode}
          onBack={() => setStage('wifi')}
          onDone={onDone}
        />
      );
    case 'sendLink':
      return (
        <SendLinkStage
          familyID={familyID}
          familyName={familyName}
          onBack={() => setStage('picker')}
          previewLink={previewLink}
        />
      );
    case 'picker':
    default:
      return (
        <PickerStage
          familyName={familyName}
          onBack={onCancel}
          onPickOwner={() => setStage('wifi')}
          onPickOther={() => setStage('sendLink')}
        />
      );
  }
}

export default HearthSetupView;
